import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import UserAutocomplete from "../components/UserAutocomplete";
import FormCarousel from "../components/FormCarousel";
import Hint from "../components/Hint";

const PRIORITIES = [
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "High" },
  { v: "urgent", label: "Urgent" },
];

const TIME_OPTIONS = [
  "09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30",
  "14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30",
  "19:00","19:30","20:00",
];

export default function CreateTaskPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/leads").then(r => setLeads(r.data || [])).catch(() => {});
    api.get("/bizdev/briefs").then(r => setBriefs(r.data || [])).catch(() => {});
    api.get("/projects").then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const initialState = {
    title: searchParams.get("title") || "",
    description: "",
    assignee: "",
    reviewer: "",
    priority: "medium",
    startDate: "",
    dueDate: "",
    dueTime: "",
    taskType: searchParams.get("project_id") ? "project" : "client",
    linkedId: searchParams.get("lead_id") || "",
    briefId: searchParams.get("brief_id") || "",
    project: searchParams.get("project_id") || "",
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-[var(--s2)] border border-[var(--bdr)] text-[var(--t1)] text-[13px] focus:outline-none focus:border-[#E31937]";
  const labelCls = "block text-[11px] font-semibold text-[var(--t2)] mb-1.5 tracking-wider uppercase";

  const buildPayload = (form, minimum = false) => ({
    title: (form.title || "").trim(),
    description: minimum ? "" : (form.description || "").trim(),
    assignee_id: form.assignee,
    reviewer_id: minimum ? form.assignee : (form.reviewer || form.assignee), // fall back to assignee for "save now"
    priority: minimum ? "medium" : form.priority,
    start_date: minimum ? null : (form.startDate || null),
    due_date: form.dueDate || null,
    due_time: minimum ? null : (form.dueTime || null),
    project_id: form.taskType === "project" ? form.project : null,
    linked_type: form.taskType === "client" && form.linkedId ? "lead" : "",
    linked_id: form.taskType === "client" ? form.linkedId : "",
    brief_id: form.taskType === "client" ? (form.briefId || null) : null,
    // Threaded from /me when promoting a jot → task. Backend soft-deletes
    // that jot atomically with this task create. (bug_cc946f51b211)
    from_jot_id: searchParams.get("from_jot_id") || null,
  });

  const submit = async (form, minimum = false) => {
    try {
      await api.post("/tasks", buildPayload(form, minimum));
      toast.success(minimum ? "Task created — fill details later" : "Task created!");
      if (form.taskType === "project" && form.project) navigate(`/projects/${form.project}`);
      else navigate("/tasks");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create task");
      throw err;
    }
  };

  // Filter active briefs by selected lead
  const filteredBriefs = (form) =>
    form.linkedId
      ? briefs.filter(b => b.lead_id === form.linkedId && !["Won","Lost","Archived"].includes(b.status))
      : [];

  const steps = [
    {
      key: "essentials",
      label: "Essentials",
      validate: (form) => {
        const errs = {};
        if (!form.title?.trim()) errs.title = "Title is required";
        if (!form.assignee) errs.assignee = "Assignee is required";
        if (!form.dueDate) errs.dueDate = "Due Date is required";
        return errs;
      },
      render: ({ form, setForm, errors }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            What's the task, who owns it, when's it due. That's it for now — fill the rest later or keep going.
          </p>
          <div>
            <label className={labelCls}>Title *</label>
            <input
              autoFocus
              className={inputCls}
              value={form.title}
              onChange={(e) => setForm({ title: e.target.value })}
              placeholder="What needs to happen?"
            />
            {errors?.title && <p className="text-[11px] text-red-400 mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className={labelCls}>Assignee *</label>
            <UserAutocomplete value={form.assignee} onChange={(v) => setForm({ assignee: v })} placeholder="Who's doing this?" />
            {errors?.assignee && <p className="text-[11px] text-red-400 mt-1">{errors.assignee}</p>}
          </div>
          <div>
            <label className={labelCls}>Due Date *</label>
            <input
              type="date"
              className={inputCls}
              value={form.dueDate}
              onChange={(e) => setForm({ dueDate: e.target.value })}
            />
            {errors?.dueDate && <p className="text-[11px] text-red-400 mt-1">{errors.dueDate}</p>}
          </div>
        </div>
      ),
    },
    {
      key: "context",
      label: "Context",
      validate: (form) => {
        const errs = {};
        if (form.taskType === "client" && !form.linkedId) errs.linkedId = "Pick a client/lead";
        if (form.taskType === "project" && !form.project) errs.project = "Pick a project";
        return errs;
      },
      render: ({ form, setForm, errors }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            Tasks must tie to something — a client/lead, a brief, or an internal project. Helps the dashboard roll up correctly.
            <Hint id="task.linked_to" width={280}>
              Tasks must tie to something — a client/lead, a brief, or an internal project. Helps the dashboard roll up correctly.
            </Hint>
          </p>
          <div>
            <label className={labelCls}>Task For *</label>
            <div className="flex border border-[var(--bdr)] rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setForm({ taskType: "client", project: "" })}
                className="flex-1 py-2.5 text-[12px] font-semibold transition-colors"
                style={{
                  background: form.taskType === "client" ? "var(--r)" : "var(--s2)",
                  color: form.taskType === "client" ? "#fff" : "var(--t3)",
                }}
              >
                Client / Lead
              </button>
              <button
                type="button"
                onClick={() => setForm({ taskType: "project", linkedId: "", briefId: "" })}
                className="flex-1 py-2.5 text-[12px] font-semibold border-l border-[var(--bdr)] transition-colors"
                style={{
                  background: form.taskType === "project" ? "var(--r)" : "var(--s2)",
                  color: form.taskType === "project" ? "#fff" : "var(--t3)",
                }}
              >
                Non-Client Project
              </button>
            </div>
          </div>

          {form.taskType === "client" && (
            <>
              <div>
                <label className={labelCls}>Client / Lead *</label>
                <select
                  className={inputCls}
                  value={form.linkedId}
                  onChange={(e) => setForm({ linkedId: e.target.value, briefId: "" })}
                >
                  <option value="">Select a client or lead…</option>
                  {leads.map((l) => (
                    <option key={l.lead_id} value={l.lead_id}>{l.company_name || l.lead_id}</option>
                  ))}
                </select>
                {errors?.linkedId && <p className="text-[11px] text-red-400 mt-1">{errors.linkedId}</p>}
              </div>
              <div>
                <label className={labelCls}>Brief {form.linkedId ? "(optional)" : ""}</label>
                <select
                  className={inputCls}
                  value={form.briefId}
                  onChange={(e) => setForm({ briefId: e.target.value })}
                  disabled={!form.linkedId}
                >
                  <option value="">
                    {!form.linkedId
                      ? "Pick a client first"
                      : filteredBriefs(form).length === 0
                        ? "No active briefs for this client"
                        : "Select a brief (optional)"}
                  </option>
                  {filteredBriefs(form).map((b) => (
                    <option key={b.brief_id} value={b.brief_id}>{b.title}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {form.taskType === "project" && (
            <div>
              <label className={labelCls}>Non-Client Project *</label>
              <select
                className={inputCls}
                value={form.project}
                onChange={(e) => setForm({ project: e.target.value })}
              >
                <option value="">Select a project…</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.name || p.title}</option>
                ))}
              </select>
              {errors?.project && <p className="text-[11px] text-red-400 mt-1">{errors.project}</p>}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "details",
      label: "Details",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            All optional. Skip if you don't need them — you can fill them in later from the task page.
          </p>
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              className={inputCls}
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ description: e.target.value })}
              placeholder="What does done look like? Add any context the assignee will need."
              style={{ resize: "vertical" }}
            />
          </div>
          <div>
            <label className={labelCls}>
              Reviewer
              <Hint id="task.reviewer">
                Reviewer marks the task done. Can be the same as assignee if you're self-managing.
              </Hint>
            </label>
            <UserAutocomplete value={form.reviewer} onChange={(v) => setForm({ reviewer: v })} placeholder="Who signs it off?" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Priority</label>
              <select
                className={inputCls}
                value={form.priority}
                onChange={(e) => setForm({ priority: e.target.value })}
              >
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                className={inputCls}
                value={form.startDate}
                onChange={(e) => setForm({ startDate: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Due Time</label>
            <select
              className={inputCls}
              value={form.dueTime}
              onChange={(e) => setForm({ dueTime: e.target.value })}
            >
              <option value="">No specific time</option>
              {TIME_OPTIONS.map((t) => {
                const [h, m] = t.split(":");
                const hr = parseInt(h);
                const ampm = hr >= 12 ? "PM" : "AM";
                const hr12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
                return <option key={t} value={t}>{`${hr12}:${m} ${ampm}`}</option>;
              })}
            </select>
          </div>
        </div>
      ),
    },
  ];

  return (
    <FormCarousel
      title="New Task"
      draftKey="task"
      finalLabel="Create Task"
      initialState={initialState}
      steps={steps}
      onSaveMinimum={(form) => submit(form, true)}
      onSaveFull={(form) => submit(form, false)}
      onClose={() => navigate(-1)}
    />
  );
}
